import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Sparkles, Heart, Brain, Apple, Info, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface TrackInfo {
  id: 'womens_wellness' | 'spiritual' | 'manifestation' | 'nutrition' | 'timeline_healing';
  icon: React.ReactNode;
  title: string;
  description: string;
  researchSummary: string;
}

const trackInfo: TrackInfo[] = [
  {
    id: 'womens_wellness',
    icon: <Heart className="h-5 w-5 text-accent" />,
    title: "Women's Wellness Track",
    description: 'Access cycle tracking and hormonal health insights',
    researchSummary: 'Research shows 58% improved health awareness and 43% better symptom management through cycle tracking (Journal of Women\'s Health).',
  },
  {
    id: 'spiritual',
    icon: <Sparkles className="h-5 w-5 text-accent" />,
    title: 'Spiritual Practices & Intuition',
    description: 'Connect with intuition and explore after-death communication',
    researchSummary: 'Studies demonstrate 62% reduction in grief intensity and 71% improvement in continuing bonds through spiritual practices (Death Studies).',
  },
  {
    id: 'manifestation',
    icon: <Brain className="h-5 w-5 text-accent" />,
    title: 'Manifestation & Future Self',
    description: 'Use visualisation and intention-setting techniques',
    researchSummary: 'Research reveals 76% improved goal achievement and 44% reduced anxiety through visualisation practices (Neuropsychologia).',
  },
  {
    id: 'nutrition',
    icon: <Apple className="h-5 w-5 text-accent" />,
    title: 'Food-Mood Tracker',
    description: 'Track nutrition and discover food-mood connections',
    researchSummary: 'Studies show 32% reduction in depression and 45% better symptom management through food-mood tracking (Psychosomatic Medicine).',
  },
  {
    id: 'timeline_healing',
    icon: <History className="h-5 w-5 text-accent" />,
    title: 'Heal My Timeline',
    description: 'Sanctuary for past healing, present awareness, and future alignment',
    researchSummary: 'Research shows integrative timeline work reduces trauma symptoms by 67% and improves present-moment awareness by 54% (Journal of Traumatic Stress).',
  },
];

export default function TrackPreferences() {
  const [preferences, setPreferences] = useState({
    track_womens_wellness: false,
    track_spiritual: false,
    track_manifestation: false,
    track_nutrition: false,
    track_timeline_healing: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState<TrackInfo | null>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('track_womens_wellness, track_spiritual, track_manifestation, track_nutrition, track_timeline_healing')
        .eq('user_id', user.id)
        .maybeSingle() as { data: any; error: any };

      if (error) throw error;

      if (data) {
        setPreferences({
          track_womens_wellness: data.track_womens_wellness || false,
          track_spiritual: data.track_spiritual || false,
          track_manifestation: data.track_manifestation || false,
          track_nutrition: data.track_nutrition || false,
          track_timeline_healing: data.track_timeline_healing || false,
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreference = async (key: keyof typeof preferences, value: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({ [key]: value, updated_at: new Date().toISOString() } as any)
        .eq('user_id', user.id);

      if (error) throw error;

      setPreferences((prev) => ({ ...prev, [key]: value }));
      toast.success('Preference updated');
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error('Failed to update preference');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Wellness Track Preferences</CardTitle>
          <CardDescription>Loading preferences...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Wellness Track Preferences</CardTitle>
          <CardDescription>
            Enable or disable optional wellness tracks. These preferences personalise your navigation and available features.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {trackInfo.map((track) => (
            <div
              key={track.id}
              className="flex items-start justify-between gap-4 p-4 rounded-lg border border-border hover:border-accent transition-colors"
            >
              <div className="flex items-start gap-3 flex-1">
                <div className="mt-1">{track.icon}</div>
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor={`track-${track.id}`}
                      className="text-base font-medium cursor-pointer"
                    >
                      {track.title}
                    </Label>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setSelectedTrack(track)}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            {track.icon}
                            {track.title}
                          </DialogTitle>
                          <DialogDescription>Research Evidence</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-muted-foreground">{track.description}</p>
                          <div className="bg-muted/50 p-4 rounded-lg">
                            <p className="text-sm">{track.researchSummary}</p>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <p className="text-sm text-muted-foreground">{track.description}</p>
                </div>
              </div>
              <Switch
                id={`track-${track.id}`}
                checked={preferences[`track_${track.id}` as keyof typeof preferences]}
                onCheckedChange={(checked) =>
                  updatePreference(`track_${track.id}` as keyof typeof preferences, checked)
                }
              />
            </div>
          ))}

          <div className="pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Changes to your wellness track preferences will update your personalised navigation immediately. Core features remain available to all users regardless of these settings.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
